import { lazy, Suspense, Component } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { ThemeProvider } from './context/ThemeContext'
import Layout from './components/Layout'
import TabNav from './components/TabNav'
import LoginPage from './pages/LoginPage'
import LandingPage from './pages/LandingPage'
import Dashboard from './pages/Dashboard'
import SalesTank from './pages/SalesTank'
import BoardDecks from './pages/BoardDecks'

// Allowed emails (lowercase) — only these users can access the portal
// KEEP IN SYNC with api/shared/investor-allowlist.js, which the API enforces
// (this list only hides the UI). npm test in api/ fails if the two drift.
const ALLOWED_EMAILS = [
    // Aviator internal
    'cbradham@aviator-energy.com',
    'thilton@aviator-energy.com',
    // Investors
    'nholeman@explorationland.com',
    'campo@cougarpipe.com',
    'msimon@explorationland.com',
    'btz@explorationland.com',
    'michaelw@cougarpipe.com',
    'mmoores@verabank.com',
    'bodie@maximusoperating.com',
    'ejoseph@maximusoperating.com',
    'bo@toatruss.com',
    'jasonbryanjones@gmail.com',
    'kimberlybellpe@gmail.com',
    'rdswisher@etmworks.com',
    'jlpalko@vogelresources.com',
    'epalko@vogelresources.com',
    'erich.palko@gmail.com',
    'jlpalko@gmail.com',
    'celeste.bradham@gmail.com',
    'brad@fondrenlp.com',
    'tdrice50@gmail.com',
    'tismith387@gmail.com',
    'blake.linnerud@gmail.com',
    'pbradham42@gmail.com',
    'jkmartin21@gmail.com',
    'kyle.walkerttu@gmail.com',
    'preston.m.bradham@gmail.com',
    'caseytx23@msn.com',
    'treyrice89@gmail.com',
    'tarabradham@gmail.com',
]

function AccessDenied({ email, onSignOut }) {
    return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center px-6">
            <img src="/aviator-energy-design.png" alt="Aviator Energy" className="h-16 w-auto mb-8 opacity-60" />
            <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-10 max-w-md text-center">
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                    <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v.01M12 9v2m0 8a8 8 0 100-16 8 8 0 000 16z" />
                    </svg>
                </div>
                <h1 className="text-2xl font-bold text-white mb-2">Access Not Authorized</h1>
                <p className="text-slate-400 mb-2">
                    The Aviator Investor Portal is restricted to approved investors.
                </p>
                <p className="text-sm text-slate-500 mb-8">
                    Signed in as <span className="text-slate-300">{email}</span>
                </p>
                <button
                    onClick={onSignOut}
                    className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                    Sign Out
                </button>
            </div>
            <p className="mt-8 text-xs text-slate-600">&copy; {new Date().getFullYear()} Aviator Energy, LLC</p>
        </div>
    )
}

// Lazy-load WellMap (large component with leaflet)
const WellMap = lazy(() =>
    import('./pages/WellMap').catch(() => {
        const reloaded = sessionStorage.getItem('map_chunk_reload')
        if (!reloaded) {
            sessionStorage.setItem('map_chunk_reload', '1')
            window.location.reload()
        }
        throw new Error('App was updated. Please refresh the page.')
    })
)

if (sessionStorage.getItem('map_chunk_reload')) {
    sessionStorage.removeItem('map_chunk_reload')
}

class MapErrorBoundary extends Component {
    state = { hasError: false, error: null }
    static getDerivedStateFromError(error) {
        return { hasError: true, error }
    }
    render() {
        if (this.state.hasError) {
            return (
                <div className="text-center py-12">
                    <p className="text-lg font-semibold text-red-400">Map failed to load</p>
                    <p className="mt-2 text-sm text-gray-400">{this.state.error?.message}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >Retry</button>
                </div>
            )
        }
        return this.props.children
    }
}

const MapLoader = () => (
    <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
)

const SafeWellMap = () => (
    <MapErrorBoundary>
        <Suspense fallback={<MapLoader />}>
            <WellMap />
        </Suspense>
    </MapErrorBoundary>
)

function AppContent() {
    const location = useLocation()
    const isLanding = location.pathname === '/'

    if (isLanding) {
        return <LandingPage />
    }

    return (
        <Layout>
            <TabNav />
            <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/map" element={<SafeWellMap />} />
                <Route path="/sales" element={<SalesTank />} />
                <Route path="/decks" element={<BoardDecks />} />
            </Routes>
        </Layout>
    )
}

function App() {
    const isAuthenticated = useIsAuthenticated()
    const { instance, accounts } = useMsal()
    const activeAccount = accounts[0]

    if (!isAuthenticated) {
        return <LoginPage />
    }

    // Check if user is in the allowed list
    const email = (activeAccount?.username || '').toLowerCase()
    if (!ALLOWED_EMAILS.includes(email)) {
        return (
            <AccessDenied
                email={activeAccount?.username || 'Unknown'}
                onSignOut={() => instance.logoutRedirect()}
            />
        )
    }

    return (
        <ThemeProvider>
            <Routes>
                <Route path="/*" element={<AppContent />} />
            </Routes>
        </ThemeProvider>
    )
}

export default App
